import React, { useEffect, useMemo, useState } from "react";
import styles from "./SitemapModule.module.scss";
import { Modul_Sitemap } from "@/wp/types/acf-module-types.ts";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Section } from "@/components/Section/Section";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { SitemapGroup } from "@/getData/getSitemapData.ts";
import { SitemapSection } from "@/modules/SitemapModule/SitemapSection/SitemapSection.tsx";
import { Input } from "@/components/Input/Input.tsx";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.tsx";

export type SidemapModuleType = Modul_Sitemap;

interface SidemapModuleProps {
  data: SidemapModuleType;
  sitemapContent: SitemapGroup[];
  isNobleElements: boolean;
}

const SitemapModule = ({
  data,
  sitemapContent,
  isNobleElements,
}: SidemapModuleProps) => {
  const content = data.sitemap;
  const { show_search } = content;
  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
  } = data.layout;

  const { getLabel, getIcon } = useGlobalContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const filteredSitemap = useMemo(() => {
    const search = debouncedSearchTerm.trim().toLowerCase();
    if (!search) return sitemapContent;

    return sitemapContent
      .map((group) => {
        // Filter items within the group
        const filteredItems = group.items?.filter((item) =>
          item.title.toLowerCase().includes(search),
        );

        const groupMatches = group.label.toLowerCase().includes(search);

        if (groupMatches || (filteredItems && filteredItems.length > 0)) {
          return {
            ...group,
            items: groupMatches ? group.items : filteredItems,
          };
        }
        return null;
      })
      .filter(Boolean) as SitemapGroup[];
  }, [debouncedSearchTerm, sitemapContent]);

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  const labels = {
    searchLabel: getLabel("sitemap_search_label", "Suche"),
    searchPlaceholder: getLabel("sitemap_placeholder", "Ich suche nach"),
    searchAriaLabel: getLabel(
      "sitemap_search_aria_label",
      "Durchsuche die Sitemap",
    ),
    noSearchResults: getLabel(
      "sitemap_no_search_results",
      "Keine Suchergebnisse gefunden",
    ),
    noDataAvailable: getLabel(
      "sitemap_no_data_available",
      "Keine Sitemap-Daten verfügbar",
    ),
  };

  const searchColumns =
    "is-12-tablet is-10-desktop is-12-widescreen is-8-fullhd";

  return (
    <Section
      id={id}
      className={`${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      containerClassName={`sitemap ${styles["sitemap"]}`}
    >
      {show_search && (
        <div className={"columns is-centered"}>
          <div
            className={`column ${searchColumns} ${styles["search-wrapper"]}`}
          >
            <label className={styles["search-label"]}>
              {labels.searchLabel}
            </label>
            <Input
              inputType={"text"}
              aria-label={labels.searchAriaLabel}
              placeholder={labels.searchPlaceholder}
              className={styles["search-input"]}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <CustomSVG
              src={getIcon("search")}
              className="is-flex"
              wrapper="span"
              width={30}
              height={30}
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      {filteredSitemap.map((group) => (
        <SitemapSection
          key={`${group.postType}-${group.label}`}
          group={group}
          isNobleElements={isNobleElements}
        />
      ))}

      {filteredSitemap.length === 0 && (
        <p className={"has-text-centered"}>
          {debouncedSearchTerm
            ? labels.noSearchResults
            : labels.noDataAvailable}
        </p>
      )}
    </Section>
  );
};

export default SitemapModule;
